/** Settings key for the public website About us section. */
export const WEBSITE_ABOUT_KEY = 'websiteAbout';

export const WEBSITE_ABOUT_TITLE_MAX = 120;
export const WEBSITE_ABOUT_BODY_MAX = 20_000;

/** Homepage heading on smymchennai.in. */
export const DEFAULT_WEBSITE_ABOUT_TITLE = 'About us';

/**
 * Exact homepage paragraph from smymchennai.in (About us), with the Bulandi
 * link kept as Markdown. Captured from the live bundle, href `/bulandi-2026`.
 */
export const DEFAULT_WEBSITE_ABOUT_BODY =
  'Recognised by Shree Maheshwari Sabha since 1985, we run outings, cultural programs, and [Bulandi](/bulandi-2026) for Maheshwari youth in Tamil Nadu, Kerala, and Pondicherry.';

export interface WebsiteAbout {
  sections?: WebsiteAboutSections;
  /** Null when the admin leaves the heading blank. */
  title: string | null;
  bodyMarkdown: string;
  /** ISO 8601. Null only for the unsaved editor default. */
  updatedAt: string | null;
}

export const DEFAULT_WEBSITE_ABOUT: WebsiteAbout = {
  title: DEFAULT_WEBSITE_ABOUT_TITLE,
  bodyMarkdown: DEFAULT_WEBSITE_ABOUT_BODY,
  updatedAt: null,
};

export function parseWebsiteAbout(valueJson: string | null | undefined): WebsiteAbout | null {
  if (!valueJson) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(valueJson);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  if (typeof record.bodyMarkdown !== 'string') return null;
  const title = typeof record.title === 'string' ? record.title.trim() : '';
  const updatedAt = typeof record.updatedAt === 'string' && record.updatedAt.trim() ? record.updatedAt : null;
  return {
    title: title || null,
    bodyMarkdown: record.bodyMarkdown,
    updatedAt,
    ...(isWebsiteAboutSections(record.sections) ? { sections: structuredClone(record.sections) } : {}),
  };
}

export function normalizeWebsiteAboutInput(
  input: unknown,
  nowIso: string,
  previous?: WebsiteAbout | null,
): { ok: true; about: WebsiteAbout } | { ok: false; error: string } {
  if (!input || typeof input !== 'object') {
    return { ok: false, error: 'title and bodyMarkdown are required' };
  }
  const record = input as Record<string, unknown>;
  if (record.bodyMarkdown != null && typeof record.bodyMarkdown !== 'string') {
    return { ok: false, error: 'bodyMarkdown must be text' };
  }
  if (record.title != null && typeof record.title !== 'string') {
    return { ok: false, error: 'title must be text' };
  }

  const title = typeof record.title === 'string' ? record.title.trim() : '';
  const bodyMarkdown = typeof record.bodyMarkdown === 'string' ? record.bodyMarkdown.replace(/\r\n/g, '\n') : previous?.bodyMarkdown ?? '';
  if ('sections' in record && !isWebsiteAboutSections(record.sections)) {
    return { ok: false, error: 'Invalid About sections' };
  }
  const sections = 'sections' in record ? record.sections as WebsiteAboutSections : previous?.sections;

  if (title.length > WEBSITE_ABOUT_TITLE_MAX) {
    return { ok: false, error: `Title must be ${WEBSITE_ABOUT_TITLE_MAX} characters or fewer` };
  }
  if (bodyMarkdown.length > WEBSITE_ABOUT_BODY_MAX) {
    return { ok: false, error: `About text must be ${WEBSITE_ABOUT_BODY_MAX} characters or fewer` };
  }

  return {
    ok: true,
    about: {
      title: title || null,
      bodyMarkdown,
      updatedAt: nowIso,
      ...(sections ? { sections: structuredClone(sections) } : {}),
    },
  };
}

export type AboutIcon = 'users' | 'target' | 'flag' | 'trending-up' | 'flower' | 'hand-heart' | 'trophy' | 'handshake';
export type AboutCard = { heading: string; text: string; icon: AboutIcon; imageUrl: string | null };
export type AboutPrinciple = { id: 'seva' | 'sangathan' | 'sandesh'; hindi: string; english: string };
export type AboutActivity = { id: 'youth' | 'leadership' | 'culture' | 'service' | 'sports' | 'representation'; label: string; icon: AboutIcon; imageUrl: string | null };
export type WebsiteAboutSections = {
  tagline: string; whoWeAre: AboutCard; vision: AboutCard; purpose: AboutCard;
  guidingHeading: string; principles: AboutPrinciple[];
  activitiesHeading: string; activities: AboutActivity[];
  closing: string; affiliation: string;
};
export const ABOUT_ICON_OPTIONS: { value: AboutIcon; label: string }[] = [
  { value: 'users', label: 'People' }, { value: 'target', label: 'Target' },
  { value: 'flag', label: 'Flag' }, { value: 'trending-up', label: 'Growth' },
  { value: 'flower', label: 'Flower' }, { value: 'hand-heart', label: 'Service' },
  { value: 'trophy', label: 'Trophy' }, { value: 'handshake', label: 'Handshake' },
];
export const DEFAULT_STRUCTURED_ABOUT_TITLE = 'About SMYM Chennai';
export const DEFAULT_WEBSITE_ABOUT_SECTIONS: WebsiteAboutSections = {
  tagline: 'Seva • Sangathan • Sandesh',
  whoWeAre: { heading: 'Who We Are', text: 'Founded in 1980 as Maheshwari Youth Club and formally recognised by Shree Maheshwari Sabha, Chennai in 1985, SMYM is the representative body of Maheshwari youth in Chennai.', icon: 'users', imageUrl: null },
  vision: { heading: 'Our Vision', text: 'To build an engaged, responsible and forward-looking generation, rooted in values and empowered to contribute.', icon: 'target', imageUrl: null },
  purpose: { heading: 'Our Purpose', text: 'Creating opportunities for connection, personal growth, leadership and community service.', icon: 'flag', imageUrl: null },
  guidingHeading: 'Our Guiding Principles',
  principles: [
    { id: 'seva', hindi: 'सेवा', english: 'Service' },
    { id: 'sangathan', hindi: 'संगठन', english: 'Unity' },
    { id: 'sandesh', hindi: 'संदेश', english: 'Message' },
  ],
  activitiesHeading: 'Our Activities',
  activities: [
    { id: 'youth', label: 'Youth Engagement', icon: 'users', imageUrl: null },
    { id: 'leadership', label: 'Leadership', icon: 'trending-up', imageUrl: null },
    { id: 'culture', label: 'Culture & Heritage', icon: 'flower', imageUrl: null },
    { id: 'service', label: 'Community Service', icon: 'hand-heart', imageUrl: null },
    { id: 'sports', label: 'Sports & Recreation', icon: 'trophy', imageUrl: null },
    { id: 'representation', label: 'Community Representation', icon: 'handshake', imageUrl: null },
  ],
  closing: 'Rooted in Values. Driven by Youth. Committed to Community.',
  affiliation: 'Under the aegis of Shree Maheshwari Sabha, Chennai',
};

export const ABOUT_IMAGE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export const ABOUT_IMAGE_PATH_PREFIX = '/api/public/site-content/about/images/';
export function isWebsiteAboutImageUrl(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith(ABOUT_IMAGE_PATH_PREFIX) && ABOUT_IMAGE_ID_PATTERN.test(value.slice(ABOUT_IMAGE_PATH_PREFIX.length));
}

function object(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function text(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.length <= max;
}
function iconImage(value: Record<string, unknown>): boolean {
  return ABOUT_ICON_OPTIONS.some(option => option.value === value.icon) && (value.imageUrl === null || isWebsiteAboutImageUrl(value.imageUrl));
}
export function isWebsiteAboutSections(value: unknown): value is WebsiteAboutSections {
  if (!object(value)) return false;
  if (!['tagline', 'closing', 'affiliation'].every(key => text(value[key], 500))) return false;
  if (!['guidingHeading', 'activitiesHeading'].every(key => text(value[key], 120))) return false;
  if (!['whoWeAre', 'vision', 'purpose'].every(key => {
    const card = value[key];
    return object(card) && text(card.heading, 120) && text(card.text, 4000) && iconImage(card);
  })) return false;
  const principleIds = ['seva', 'sangathan', 'sandesh'];
  const activityIds = ['youth', 'leadership', 'culture', 'service', 'sports', 'representation'];
  return Array.isArray(value.principles) && value.principles.length === 3 && value.principles.every((p, i) => object(p) && p.id === principleIds[i] && text(p.hindi, 120) && text(p.english, 120)) &&
    Array.isArray(value.activities) && value.activities.length === 6 && value.activities.every((a, i) => object(a) && a.id === activityIds[i] && text(a.label, 120) && iconImage(a));
}
